import { Link } from 'react-router-dom';
import Seo from '../components/common/Seo';
import BlogCard from '../components/common/BlogCard';
import CardSkeleton from '../components/common/CardSkeleton';
import CategoryNav from '../components/common/CategoryNav';
import FeaturedCarousel from '../components/blogs/FeaturedCarousel';
import { LATEST_LIMIT, useBlogs } from '../context/BlogsContext';
import { SITE_NAME, SITE_TAGLINE, SITE_DESCRIPTION } from '../constants/site';
import { collectionSchema, organisationSchema, websiteSchema } from '../utils/jsonLd';

// The carousel shows at most this many pinned posts -- whatever the backend
// actually returns as `featured: true`, capped here as a safety net. Nothing
// is topped up from the latest posts: a short (or empty) pinned list just
// means a short (or absent) featured row, which is a normal state.
const FEATURED_LIMIT = 6;

/**
 * Front page.
 *
 * Two independent blocks. On top, a sliding row of up to six featured cards
 * (real pins first, topped up from the newest posts). Below, the newest
 * articles, unchanged. The two come from separate requests and load
 * separately, so neither waits on the other and a failure in one does not
 * blank the other.
 */
function HomePage() {
  const { featuredList, featuredLoading, latest, hasMore, loading, error, reload } = useBlogs();

  // Only real pins, capped at six. Nothing is topped up from `latest` -- if
  // the backend has fewer than six featured posts, the row just shows fewer.
  const topFeatured = featuredList.slice(0, FEATURED_LIMIT);

  /*
   * The grid is the newest LATEST_LIMIT posts as the table returns them, pinned
   * ones included. A featured post that is also new therefore shows twice — once
   * on top, once here — and that is the intent: "Latest articles" has to mean the
   * latest, and filtering the pins out is what made the grid shrink whenever
   * something was featured.
   */
  const gridPosts = latest;

  // Only invite the reader onward when the API says there is something behind it.
  const moreToRead = hasMore;

  // WebSite + Organization are emitted once, on the home page, and referenced by
  // @id from every other page's schema.
  const schemas = [
    organisationSchema(),
    websiteSchema(),
    collectionSchema({
      name: `${SITE_NAME} — Latest articles`,
      description: SITE_DESCRIPTION,
      path: '/',
      posts: latest,
    }),
  ];

  return (
    <div className="container page">
      <Seo path="/" description={SITE_DESCRIPTION} jsonLd={schemas} />

      <section className="hero">
        <p className="hero-eyebrow">{SITE_TAGLINE}</p>
        <h1 className="hero-title">
          Everything worth reading,
          <br />
          all in one place.
        </h1>
        <p className="hero-sub">{SITE_DESCRIPTION}</p>
        <div className="hero-actions">
          <Link className="btn btn-primary" to="/blogs">
            Browse all articles
          </Link>
          <Link className="btn btn-ghost" to="/about">
            About {SITE_NAME}
          </Link>
        </div>
      </section>

      <CategoryNav ariaLabel="Browse by category" />

      {/* --- Featured ------------------------------------------------------ */}
      {featuredLoading && (
        <section aria-busy="true" aria-label="Loading featured articles">
          <div className="section-head">
            <span className="section-title skeleton skeleton-heading" />
          </div>
          <div className="featured-carousel">
            <div className="featured-carousel-track">
              {Array.from({ length: 3 }).map((_, i) => (
                <div className="featured-carousel-item" key={i}>
                  <CardSkeleton />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {!featuredLoading && topFeatured.length > 0 && (
        <section aria-labelledby="featured-heading">
          <div className="section-head">
            <h2 className="section-title" id="featured-heading">
              Featured
            </h2>
            <Link className="section-link" to="/blogs">
              View all <span aria-hidden="true">→</span>
            </Link>
          </div>

          <FeaturedCarousel posts={topFeatured} label="Featured articles" />
        </section>
      )}


      {/* --- Latest -------------------------------------------------------- */}
      <section aria-labelledby="latest-heading">
        <div className="section-head">
          <h2 className="section-title" id="latest-heading">
            Latest articles
          </h2>
          <Link className="section-link" to="/blogs">
            View all <span aria-hidden="true">→</span>
          </Link>
        </div>

        {/* One placeholder per slot the grid is about to fill — reserving three
            and then rendering nine is the layout shift the skeletons exist to
            prevent. */}
        {loading && (
          <div className="blog-grid" aria-busy="true" aria-label="Loading articles">
            {Array.from({ length: LATEST_LIMIT }, (_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        )}

        {error && (
          <div className="state-msg state-error" role="alert">
            <p>{error}</p>
            <button type="button" className="btn btn-ghost" onClick={reload}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && gridPosts.length > 0 && (
          <div className="blog-grid">
            {gridPosts.map((b) => (
              <BlogCard key={b.blogId} blog={b} headingLevel="h3" />
            ))}
          </div>
        )}

        {!loading && !error && !gridPosts.length && !topFeatured.length && (
          <div className="state-msg">
            <p>No articles published yet.</p>
          </div>
        )}
      </section>

      {/*
        * The way off this page. The grid above is a fixed slice of the newest
        * posts, so it dead-ends without something like this; the category strip
        * underneath is the same idea for readers who want one section rather than
        * everything, and it costs no extra requests.
        */}
      <section className="see-more" aria-labelledby="see-more-heading">
        <h2 className="see-more-title" id="see-more-heading">
          {moreToRead ? 'Want to see more blogs?' : 'Browse by section'}
        </h2>
        <p className="see-more-text">
          {moreToRead
            ? 'That is just the newest few. Every article on the site is in one list.'
            : 'Every section has its own page, with everything filed under it.'}
        </p>

        {moreToRead && (
          <Link className="btn btn-primary btn-lg" to="/blogs">
            See all articles <span aria-hidden="true">→</span>
          </Link>
        )}

        <CategoryNav includeAll={false} ariaLabel="All categories" />
      </section>
    </div>
  );
}

export default HomePage;
